/**
 * ListQuickFiltersBar Component
 * 
 * Renders quick filter pills/chips above list tables.
 * Quick filters are schema-driven presets that replace current filters.
 * 
 * Behavior:
 * - Renders nothing if quickFilters is empty
 * - Exactly one pill active at a time
 * - Clicking a pill calls onChange with pill's filters
 * - Visual styling based on active state and pillVariant
 */

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ListViewQuickFilterConfig, ListViewFilter } from "@/lib/views/listViewSchemas/types";

interface ListQuickFiltersBarProps {
  doctype: string;
  quickFilters: ListViewQuickFilterConfig[];
  activeFilterId: string | null;
  onChange: (nextId: string | null, nextFilters: ListViewFilter[]) => void;
}

export function ListQuickFiltersBar({
  doctype: _doctype,
  quickFilters = [],
  activeFilterId,
  onChange,
}: ListQuickFiltersBarProps) {
  if (!quickFilters.length) return null;

  return (
    <div
      className={cn(
        // Mobile: single-row horizontal scroll strip (swipe through pills)
        // instead of wrapping into several stacked rows.
        // sm+: revert to the wrapping bar.
        "flex flex-nowrap gap-2 mb-4 overflow-x-auto scrollbar-none snap-x",
        "sm:flex-wrap sm:overflow-visible",
      )}
    >
      {quickFilters.map((filter) => {
        const isActive = filter.id === activeFilterId;
        
        // Determine variant based on active state and pillVariant
        const variant = isActive 
          ? "default" 
          : filter.pillVariant === "outline" 
            ? "outline" 
            : "ghost";

        return (
          <Button
            key={filter.id}
            type="button"
            variant={variant}
            size="sm"
            // font-normal overrides the Button base's font-medium — these filter
            // chips read lighter (per design feedback); the active chip is already
            // distinguished by its filled background.
            // shrink-0 keeps pills full-size in the mobile scroll strip;
            // snap-start gives a tidy swipe stop.
            className={cn("rounded-full font-normal shrink-0 snap-start")}
            onClick={() => {
              onChange(filter.id, filter.filters);
            }}
          >
            {filter.label}
          </Button>
        );
      })}
    </div>
  );
}

